import { VIZ_LIMITS, VIZ_PROTOCOL, VIZ_RUNNER_PATH } from '../limits.ts';
import {
  CONNECT,
  isReady,
  isRefused,
  parseRenderResult,
  type RenderFailureKind,
  type RenderRequest,
} from '../protocol.ts';
import { sanitizeVizSvg, UnsafeSvgError, type SanitizedSvg } from './sanitize.ts';

/**
 * The host side of a model-authored visualization (ADR 0013): one hidden relay iframe per page
 * (sandbox="allow-scripts", the runner page at /viz-runner/ with its own CSP), a private
 * MessagePort to it, one nonce per render, and the SVG that comes back sanitized before anything
 * of it touches the page.
 *
 * The handshake is the only window message: the runner's `ready` is accepted only when its
 * `source` is the relay iframe's own contentWindow and its origin is 'null' (the sandbox), and the
 * host answers with a port and no data. Every render and result then travels on that port, which
 * only the runner document holds; a result counts only for the nonce of a pending render.
 */
export interface RenderVizInput {
  code: string;
  data: unknown;
  width?: number;
  height?: number;
  signal?: AbortSignal;
}

export type RenderVizOutcome =
  | ({ ok: true; ms: number; hostMs: number } & SanitizedSvg)
  | {
      ok: false;
      kind: RenderFailureKind | 'unsafe_svg' | 'aborted';
      message: string;
      line?: number;
      ms: number;
      hostMs: number;
    };

interface Pending {
  resolve(o: RenderVizOutcome): void;
  started: number;
  timer: ReturnType<typeof setTimeout>;
}

const clamp = (v: number | undefined, r: { min: number; max: number; default: number }) =>
  Math.round(Math.min(r.max, Math.max(r.min, Number.isFinite(v) ? (v as number) : r.default)));

export interface VizHostOptions {
  /** Default VIZ_RUNNER_PATH, on this page's origin. */
  runnerPath?: string;
  win?: Window & typeof globalThis;
  /** Extra time the host waits beyond the runner's own limit before it gives up. */
  graceMs?: number;
  readyTimeoutMs?: number;
}

export class VizHost {
  private readonly win: Window & typeof globalThis;
  private readonly runnerPath: string;
  private readonly graceMs: number;
  private readonly readyTimeoutMs: number;
  private frame: HTMLIFrameElement | null = null;
  private port: MessagePort | null = null;
  private ready: Promise<MessagePort> | null = null;
  private readonly pending = new Map<string, Pending>();
  private loads = 0;
  private onWindowMessage: ((e: MessageEvent) => void) | null = null;

  constructor(opts: VizHostOptions = {}) {
    this.win = opts.win ?? window;
    this.runnerPath = opts.runnerPath ?? VIZ_RUNNER_PATH;
    this.graceMs = opts.graceMs ?? 3_000;
    this.readyTimeoutMs = opts.readyTimeoutMs ?? 15_000;
  }

  /** The relay iframe, created on first use. */
  private connect(): Promise<MessagePort> {
    if (this.ready) return this.ready;
    this.ready = new Promise<MessagePort>((resolve, reject) => {
      const doc = this.win.document;
      const frame = doc.createElement('iframe');
      frame.setAttribute('sandbox', 'allow-scripts');
      frame.setAttribute('referrerpolicy', 'no-referrer');
      frame.setAttribute('aria-hidden', 'true');
      frame.setAttribute('tabindex', '-1');
      frame.setAttribute('title', 'visualization sandbox');
      frame.dataset.testid = 'viz-relay';
      frame.style.cssText = 'position:fixed;width:0;height:0;border:0;left:-10px;top:-10px;visibility:hidden';
      const url = new URL(this.runnerPath, this.win.location.href);
      url.searchParams.set('host', this.win.location.origin);
      frame.src = url.toString();
      const timer = setTimeout(() => {
        this.reset('The visualization sandbox did not start.');
        reject(new Error('The visualization sandbox did not start.'));
      }, this.readyTimeoutMs);
      this.onWindowMessage = (e: MessageEvent) => {
        if (!this.frame || e.source !== this.frame.contentWindow || e.origin !== 'null') return;
        if (isRefused(e.data) && !this.port) {
          clearTimeout(timer);
          const why = 'The visualization sandbox is served without its security policy; nothing was run.';
          this.reset(why);
          reject(new Error(why));
          return;
        }
        if (!isReady(e.data) || this.port) return;
        const channel = new MessageChannel();
        this.port = channel.port1;
        this.port.onmessage = (m) => this.onResult(m.data);
        // The sandboxed runner's origin is opaque: no origin string names it, so '*'. This message
        // carries no data, only the private port, and goes to the relay's own contentWindow.
        this.frame.contentWindow!.postMessage({ v: VIZ_PROTOCOL, type: CONNECT }, '*', [channel.port2]);
        clearTimeout(timer);
        resolve(this.port);
      };
      this.win.addEventListener('message', this.onWindowMessage);
      // A second load means the frame navigated: nothing on the old channel is trusted any more.
      frame.addEventListener('load', () => {
        this.loads++;
        if (this.loads > 1) this.reset('The visualization sandbox reloaded.');
      });
      this.frame = frame;
      doc.body.appendChild(frame);
    });
    this.ready.catch(() => undefined);
    return this.ready;
  }

  private onResult(raw: unknown) {
    const r = parseRenderResult(raw);
    if (!r) return;
    const p = this.pending.get(r.nonce);
    if (!p) return;
    this.pending.delete(r.nonce);
    clearTimeout(p.timer);
    const hostMs = Math.round(performance.now() - p.started);
    if (!r.ok) return p.resolve({ ok: false, ...r.error, ms: r.ms, hostMs });
    try {
      const clean = sanitizeVizSvg(r.svg, this.win);
      p.resolve({ ok: true, ...clean, ms: r.ms, hostMs });
    } catch (e) {
      p.resolve({
        ok: false,
        kind: 'unsafe_svg',
        message:
          e instanceof UnsafeSvgError ? e.message : `The chart could not be shown: ${(e as Error).message}`,
        ms: r.ms,
        hostMs,
      });
    }
  }

  /** Drops the relay (and fails what was pending); the next render starts a fresh one. */
  reset(reason = 'The visualization sandbox was reset.') {
    for (const [nonce, p] of this.pending) {
      clearTimeout(p.timer);
      p.resolve({
        ok: false,
        kind: 'crashed',
        message: reason,
        ms: 0,
        hostMs: Math.round(performance.now() - p.started),
      });
      this.pending.delete(nonce);
    }
    if (this.onWindowMessage) this.win.removeEventListener('message', this.onWindowMessage);
    this.onWindowMessage = null;
    this.port?.close();
    this.port = null;
    this.frame?.remove();
    this.frame = null;
    this.ready = null;
    this.loads = 0;
  }

  async render(input: RenderVizInput): Promise<RenderVizOutcome> {
    const started = performance.now();
    const fail = (kind: 'error' | 'aborted' | 'crashed', message: string): RenderVizOutcome => ({
      ok: false,
      kind,
      message,
      ms: 0,
      hostMs: Math.round(performance.now() - started),
    });
    if (typeof input.code !== 'string' || !input.code.trim())
      return fail('error', 'There is no code to run.');
    if (input.code.length > VIZ_LIMITS.codeChars)
      return fail('error', `The code is longer than ${VIZ_LIMITS.codeChars} characters.`);
    let port: MessagePort;
    try {
      port = await this.connect();
    } catch (e) {
      return fail('crashed', (e as Error).message);
    }
    if (input.signal?.aborted) return fail('aborted', 'Stopped.');
    const nonce = crypto.randomUUID();
    const req: RenderRequest = {
      v: VIZ_PROTOCOL,
      type: 'render',
      nonce,
      code: input.code,
      data: input.data ?? null,
      width: clamp(input.width, VIZ_LIMITS.width),
      height: clamp(input.height, VIZ_LIMITS.height),
    };
    return new Promise<RenderVizOutcome>((resolve) => {
      const timer = setTimeout(() => {
        if (!this.pending.delete(nonce)) return;
        // The runner answers a runaway render itself; silence past that means it is stuck.
        this.reset('The visualization sandbox stopped answering.');
        resolve(fail('crashed', 'The visualization sandbox stopped answering.'));
      }, VIZ_LIMITS.timeoutMs + this.graceMs);
      this.pending.set(nonce, { resolve, started, timer });
      input.signal?.addEventListener('abort', () => {
        const p = this.pending.get(nonce);
        if (!p) return;
        this.pending.delete(nonce);
        clearTimeout(p.timer);
        resolve(fail('aborted', 'Stopped.'));
      });
      try {
        port.postMessage(req);
      } catch (e) {
        this.pending.delete(nonce);
        clearTimeout(timer);
        resolve(fail('error', `The data cannot be sent to the sandbox: ${(e as Error).message}`));
      }
    });
  }
}

let shared: VizHost | null = null;
/** The page's one host (one relay iframe for every viz on the page). */
export function vizHost(): VizHost {
  return (shared ??= new VizHost());
}
